/**
 * Design Approval Modal
 * Modal for approving 2D or 3D designs with optional feedback and price adjustment
 */

import React, { useState } from 'react';
import { useProjects } from '../../context/ProjectContext';

const DesignApprovalModal = ({ isOpen, onClose, projectId, designType, currentPrice, onSuccess }) => {
  const { approve2D, approve3D, updatePackagePrice } = useProjects();

  const [feedback, setFeedback] = useState('');
  const [adjustPrice, setAdjustPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(currentPrice || '');
  const [priceReason, setPriceReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const isThreeD = designType === '3d';
  const title = isThreeD ? 'Approve 3D Design' : 'Approve 2D Drawing';

  // Validate inputs
  const validateInputs = () => {
    if (adjustPrice) {
      if (!newPrice || isNaN(newPrice) || parseFloat(newPrice) <= 0) {
        setError('Please enter a valid price');
        return false;
      }
      if (!priceReason.trim()) {
        setError('Please provide a reason for the price adjustment');
        return false;
      }
    }
    return true;
  };

  // Handle approval
  const handleApprove = async () => {
    if (!validateInputs()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Update price if adjusted
      if (adjustPrice && parseFloat(newPrice) !== (parseFloat(currentPrice) || 0)) {
        await updatePackagePrice(projectId, parseFloat(newPrice), priceReason);
      }

      // Approve design with optional notes
      if (isThreeD) {
        await approve3D(projectId, feedback.trim() || null);
      } else {
        await approve2D(projectId, feedback.trim() || null);
      }

      // Success callback
      if (onSuccess) {
        onSuccess({
          designType,
          feedback,
          priceAdjusted: adjustPrice,
          newPrice: adjustPrice ? parseFloat(newPrice) : null
        });
      }

      handleClose();
    } catch (err) {
      console.error('Error approving design:', err);
      setError(err.message || 'Failed to approve design');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setFeedback('');
    setAdjustPrice(false);
    setNewPrice(currentPrice || '');
    setPriceReason('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-green-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Info box */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800">
              You are about to approve the {isThreeD ? '3D design' : '2D drawing'}. This will move the project to the next phase.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Feedback */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Approval Notes (Optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder="Add any notes or feedback about the approved design..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* Price adjustment toggle */}
          <div className="border-t border-gray-200 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={adjustPrice}
                onChange={(e) => setAdjustPrice(e.target.checked)}
                disabled={submitting}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
              />
              <div>
                <p className="font-medium text-gray-900">Adjust Package Price</p>
                <p className="text-sm text-gray-500">Update the project price during approval</p>
              </div>
            </label>
          </div>

          {/* Price adjustment fields */}
          {adjustPrice && (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Price
                </label>
                <div className="text-2xl font-bold text-gray-900">
                  RM {(parseFloat(currentPrice) || 0).toLocaleString()}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Price (RM) *
                </label>
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Price Change *
                </label>
                <textarea
                  value={priceReason}
                  onChange={(e) => setPriceReason(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  placeholder="Explain why the price is being adjusted..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              {/* Price change indicator */}
              {newPrice && !isNaN(newPrice) && parseFloat(newPrice) !== (parseFloat(currentPrice) || 0) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Price Change:</span>
                    <span className={`font-semibold ${parseFloat(newPrice) > (parseFloat(currentPrice) || 0) ? 'text-red-600' : 'text-green-600'}`}>
                      {parseFloat(newPrice) > (parseFloat(currentPrice) || 0) ? '+' : ''}
                      RM {(parseFloat(newPrice) - (parseFloat(currentPrice) || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={submitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Approving...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Approve {isThreeD ? '3D Design' : '2D Drawing'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesignApprovalModal;
